import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Icono lucide-react opcional a la izquierda del texto. */
  leadingIcon?: ReactNode;
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-sans font-semibold ' +
  'transition-colors duration-fast select-none ' +
  'focus-visible:outline-none focus-visible:shadow-focus ' +
  'disabled:opacity-50 disabled:pointer-events-none';

const variants: Record<ButtonVariant, string> = {
  // Azul Participación — primario de acción.
  primary: 'bg-primary text-content-on-primary hover:bg-primary-700',
  // Gris Evidencia sobre superficie, borde sobrio.
  secondary:
    'bg-surface-raised text-content border border-border-strong hover:bg-surface-sunken',
  ghost: 'bg-transparent text-primary hover:bg-surface-sunken',
  danger: 'bg-error text-content-on-primary hover:opacity-90',
};

const sizes: Record<ButtonSize, string> = {
  // Todos cumplen target táctil >=44px (min-h-touch) para mobile-first.
  sm: 'min-h-touch px-3 text-sm',
  md: 'min-h-touch px-4 text-base',
  lg: 'min-h-touch px-6 text-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leadingIcon,
    fullWidth = false,
    disabled,
    children,
    className,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        base,
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        leadingIcon
      )}
      <span>{children}</span>
    </button>
  );
});
